// Publikus terület: a kategóriái a cikkeikkel.
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublicSpace } from '@/features/public/api';
import { PublicShell } from '@/features/public/components/public-shell';
import { useHashScroll } from '@/lib/use-hash-scroll';

export const Route = createFileRoute('/sugo/$spaceSlug/')({
  component: PublicSpacePage,
});

function PublicSpacePage(): JSX.Element {
  const { t } = useTranslation();
  const { spaceSlug } = Route.useParams();
  const query = usePublicSpace(spaceSlug);
  const page = query.data?.status === 200 ? query.data.body : null;
  useHashScroll(page !== null);

  if (query.isLoading) {
    return (
      <PublicShell>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="mt-8 h-48 w-full" />
      </PublicShell>
    );
  }

  if (!page) {
    return (
      <PublicShell>
        <p className="text-muted-foreground">{t('public.notFound')}</p>
        <Link to="/sugo" className="text-primary mt-4 inline-block underline underline-offset-2">
          {t('public.backToHelp')}
        </Link>
      </PublicShell>
    );
  }

  return (
    <PublicShell internalTarget={{ spaceSlug: page.space.slug }}>
      <KnowledgeCrumbs variant="public" path={{ space: page.space }} />
      <h1 className="text-read-2xl font-semibold tracking-tight">{page.space.name}</h1>
      {page.space.description && (
        <p className="text-muted-foreground text-read-sm mt-2">{page.space.description}</p>
      )}

      <div className="mt-10 space-y-10">
        {page.categories.map((category) => (
          // Horgony-id: a cikk-oldali morzsa könyv-linkje ide ugrik (mint a belső szekció-oldalon).
          <section key={category.id} id={category.slug} className="scroll-mt-24">
            <h2 className="text-subtle text-2xs border-border border-b pb-2 font-medium uppercase tracking-[0.14em]">
              {category.name}
            </h2>
            <ul className="divide-border divide-y">
              {category.articles.map((article) => (
                <li key={article.id}>
                  <Link
                    to="/sugo/$spaceSlug/$categorySlug/$articleSlug"
                    params={{
                      spaceSlug: page.space.slug,
                      categorySlug: category.slug,
                      articleSlug: article.slug,
                    }}
                    className="index-row hover:bg-muted/50 group block py-3"
                  >
                    <span className="group-hover:text-primary font-medium transition-colors duration-150">
                      {article.title}
                    </span>
                    {article.summary && (
                      <span className="text-muted-foreground mt-1 block text-sm">
                        {article.summary}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </PublicShell>
  );
}
